import React from 'react';
import { Link } from 'react-router-dom';
import BuildCollectibleMini from '../vehicle/BuildCollectibleMini';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import type { PassportEncounter } from '../../services/api/passport';
import { formatEncounterDate, SUBJECT_TYPE_LABELS } from './passportCopy';
import { encounterDisplayTitle } from './passportFeedUtils';

type Props = {
  encounters: PassportEncounter[];
  onOpen?: (publicId: string, subjectType: string) => void;
};

/**
 * Horizontal “Latest discoveries” rail — same collectible plate language as scan reveal.
 */
export const PassportDiscoveriesRail: React.FC<Props> = ({ encounters, onOpen }) => {
  const vehicles = encounters.filter((e) => e.subject_type === 'vehicle').slice(0, 12);
  if (vehicles.length === 0) return null;

  return (
    <section className="mb-10" aria-labelledby="passport-discoveries-heading">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h2 id="passport-discoveries-heading" className="text-lg font-semibold text-white">
            Latest discoveries
          </h2>
          <p className="mt-1 text-sm text-zinc-500">Builds that entered your Passport recently.</p>
        </div>
      </div>

      <div className="-mx-1 flex gap-3 overflow-x-auto pb-2 px-1 snap-x snap-mandatory scrollbar-thin">
        {vehicles.map((encounter) => {
          const subject = encounter.subject;
          const collectible = subject?.collectible;
          const image =
            (typeof collectible?.image_url === 'string' && collectible.image_url) ||
            subject?.image_url ||
            subject?.thumbnail_url ||
            null;
          const title =
            (typeof collectible?.title === 'string' && collectible.title) ||
            encounterDisplayTitle(encounter);
          const year =
            (typeof collectible?.year === 'number' && collectible.year) || subject?.year || 0;
          const make =
            (typeof collectible?.make === 'string' && collectible.make) || subject?.make || '';
          const model =
            (typeof collectible?.model === 'string' && collectible.model) || subject?.model || '';
          const trim =
            (typeof collectible?.trim === 'string' && collectible.trim) || subject?.trim || null;
          const mods =
            (typeof collectible?.modification_count === 'number'
              ? collectible.modification_count
              : null) ??
            subject?.modification_count ??
            0;
          const cert =
            (typeof collectible?.certification_status === 'string'
              ? collectible.certification_status
              : null) ??
            subject?.certification_status ??
            null;
          const stars =
            (typeof collectible?.rarity_stars === 'number' ? collectible.rarity_stars : null) ??
            subject?.rarity_stars ??
            null;

          return (
            <Link
              key={encounter.public_id}
              to={`/passport/encounters/${encodeURIComponent(encounter.public_id)}`}
              className="snap-start shrink-0 w-[168px] sm:w-[180px] rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60 transition-transform duration-200 hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              onClick={() => onOpen?.(encounter.public_id, encounter.subject_type)}
            >
              <BuildCollectibleMini
                title={title}
                year={year || new Date().getFullYear()}
                make={make || SUBJECT_TYPE_LABELS.vehicle}
                model={model || ''}
                trim={trim}
                imageUrl={image ? resolvePublicFileUrl(image) || image : null}
                modificationCount={mods}
                certificationStatus={cert}
                rarityStars={stars}
              />
              <p className="mt-2 px-0.5 text-[11px] text-zinc-500 truncate">
                Collected {formatEncounterDate(encounter.occurred_at)}
              </p>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default PassportDiscoveriesRail;
